import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Row } from '@tanstack/react-table';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { Link } from 'react-router-dom';
import { VoucherType } from 'src/features/voucherTypes/utils/types';
import DeleteVoucherTypeFormModal from 'src/features/voucherTypes/components/forms/DeleteVoucherTypeFormModal';
import UpdateVoucherTypeFormModal from 'src/features/voucherTypes/components/forms/UpdateVoucherTypeFormModal';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import React from 'react';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type CellVoucherTypeActions = {
    row: Row<VoucherType>;
};

const isPastVoucherType = (validTo: Date) => {
    const today = new Date();
    const compareDate = new Date(validTo);

    return compareDate < today;
};

export default function CellVoucherTypeActions({ row }: CellVoucherTypeActions) {
    const { formatMessage } = useIntl();
    const permissionManageVouchers = useHasPermissions([PERMISSION.MANAGE_VOUCHERS]);

    const [isPastVoucher, setIsPastVoucher] = React.useState(false);

    React.useEffect(() => {
        if (!row.original.valid_to) return;

        const voucherTypeValidTo = row.original.valid_to ? new Date(row.original.valid_to) : new Date();

        setIsPastVoucher(isPastVoucherType(voucherTypeValidTo));
    }, [row.original.valid_to]);

    return (
        <div className="flex w-full justify-end gap-1">
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Link to={`/voucher-types/${row.original.id}`}>
                            <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                <FontAwesomeIcon icon={ICON_MAP.EXPAND} />
                            </Button>
                        </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                        <FormattedMessage {...generalMessages.dashboard} />
                    </TooltipContent>
                </Tooltip>

                {permissionManageVouchers && !isPastVoucher && (
                    <>
                        <Tooltip>
                            <UpdateVoucherTypeFormModal
                                semantics={{ entryName: formatMessage(voucherTypesMessages.voucher).toLowerCase() }}
                                selectedVoucherType={row.original}
                            >
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                                    </Button>
                                </TooltipTrigger>
                            </UpdateVoucherTypeFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.edit} />
                            </TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <DeleteVoucherTypeFormModal selectedVoucherType={row.original}>
                                <TooltipTrigger asChild>
                                    <Button size="icon" variant="link" className="cursor-pointer hover:bg-gray-100">
                                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                                    </Button>
                                </TooltipTrigger>
                            </DeleteVoucherTypeFormModal>
                            <TooltipContent>
                                <FormattedMessage {...generalMessages.delete} />
                            </TooltipContent>
                        </Tooltip>
                    </>
                )}
            </TooltipProvider>
        </div>
    );
}
